import { Modal } from '@byecode/ui'
import type { ChartBlockAbstract, DataSourceAbstract, Field, FieldADTValue, Pagination } from '@lighthouse/core'
import { DataSourceType, SelectedMode } from '@lighthouse/core'
import type { ColumnEvent, CreateFieldParams, InsertDirection, RowEvent } from '@lighthouse/shared'
import {
    createFieldNeedFetchData,
    dataDrawerPubSub,
    SuspendPagination,
    Table,
    updateFieldNeedFetchData,
    useAtomAction,
    useAtomData,
    useHandleAbortPrevious
} from '@lighthouse/shared'
import React, { useCallback, useEffect, useId, useMemo, useRef } from 'react'
import { useMount } from 'react-use'
import styled from 'styled-components'
import { useImmer } from 'use-immer'

import {
    createFieldAtom,
    createRecordAtom,
    deleteFieldAtom,
    deleteRecordAtom,
    fetchDataSourceAtom,
    loadDataSourceAtom,
    updateCellAtom,
    updateFieldAtom
} from '@/atoms/dataSource/action'
import { recordPoolAtom, viewIndependentDataAtom } from '@/atoms/dataSource/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useFilter } from '@/hooks/useFilter'
import { useLinkFilter } from '@/hooks/useLinkFilter'
import * as srv from '@/services'
import { getPexelsList } from '@/services'
import { uploadManagerInAppParams } from '@/utils/auth'

const SCxContainer = styled.div`
    flex: 1;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-bottom: 14px;
`

const SCxFooter = styled.div`
    position: absolute;
    z-index: 2;
    bottom: 15px;
    left: 50%;
    transform: translate(-50%, -50%);
`

interface ChartDataSourceProps {
    blockData: ChartBlockAbstract
    dataSource: DataSourceAbstract
    pageId: string
}

interface ChartDataSourceState {
    selectedRecords: string[]
    selectedMode?: SelectedMode
}

export const ChartDataSource: React.FC<ChartDataSourceProps> = ({ blockData, dataSource, pageId }) => {
    const { id, schema, type, name } = dataSource
    const {
        config: { ruleFilter, linkFilterController }
    } = blockData
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const isAggregateDataSource = type === DataSourceType.aggregateDataSource

    const uploadId = useId()
    const dataSourceList = useDataSourceList(appId, envId)
    const viewIndependentData = useAtomData(
        viewIndependentDataAtom,
        useCallback(s => s[id] || { recordPoints: [], rowTotal: 0 }, [id])
    )
    const { recordPoints, rowTotal } = viewIndependentData
    const { run: fetchDataSource, loading } = useAtomAction(fetchDataSourceAtom)
    const { run: loadDataSource } = useAtomAction(loadDataSourceAtom)
    const { run: createField } = useAtomAction(createFieldAtom)
    const { run: updateField } = useAtomAction(updateFieldAtom)
    const { run: deleteField } = useAtomAction(deleteFieldAtom)
    const { run: updateCell } = useAtomAction(updateCellAtom)
    const { run: createRecord } = useAtomAction(createRecordAtom)
    const { run: deleteRecord } = useAtomAction(deleteRecordAtom)
    const selectedModeRef = useRef<SelectedMode>()
    const { mutation, abortRef } = useHandleAbortPrevious(fetchDataSource)
    const createFieldParamsRef = useRef<CreateFieldParams>({
        sourceId: '',
        direction: '',
        fieldId: ''
    })
    const [state, setState] = useImmer<ChartDataSourceState>({
        selectedRecords: [],
        selectedMode: undefined
    })

    // 筛选器hook
    const { resolvedFilter } = useFilter({
        filter: ruleFilter,
        dataSourceList
    })

    // 联动筛选器hook
    const { resolvedLinkFilter } = useLinkFilter({
        appId,
        envId,
        pageId,
        linkFilterController
    })

    const { selectedRecords, selectedMode } = state

    const getDataSource = useCallback(() => {
        mutation({
            envId,
            dsId: id,
            pagination: { currentPage: 1, pageSize: 100 },
            filter: ruleFilter,
            linkFilter: resolvedLinkFilter
        })
    }, [mutation, envId, id, ruleFilter, resolvedLinkFilter])

    useMount(() => {
        return () => {
            if (abortRef.current) {
                abortRef.current.abort()
            }
        }
    })

    useEffect(() => {
        if (id) {
            getDataSource()
        }
    }, [id, getDataSource])

    const handleLoadDataSource = useCallback(
        (pagination: Pagination) => {
            return loadDataSource({
                envId,
                dsId: id,
                pagination,
                filter: ruleFilter,
                linkFilter: resolvedLinkFilter
            })
        },
        [envId, id, loadDataSource, resolvedLinkFilter, ruleFilter]
    )

    const handleSelectedRecords = useCallback(
        (selectedRecords: string[]) => {
            setState(draft => {
                draft.selectedRecords = selectedRecords
                draft.selectedMode = SelectedMode.CURRENT_PAGE
            })
            selectedModeRef.current = SelectedMode.CURRENT_PAGE
        },
        [setState]
    )

    const handleCellChange = useCallback(
        async (recordId: string, fieldValue: FieldADTValue) => {
            const isUpdate = await updateCell({ envId, dsId: id, recordId, fieldId: fieldValue.id, value: fieldValue })
            if (isUpdate) {
                dataDrawerPubSub.emit(`${id}-UPDATE`)
            }
            return isUpdate
        },
        [envId, id, updateCell]
    )

    const handleCellUpdate = useCallback(
        async (recordId: string, fieldValue: FieldADTValue) => {
            const isUpdate = await srv.updateCell({
                envId,
                dsId: id,
                id: recordId,
                fieldId: fieldValue.id,
                content: fieldValue
            })
            if (isUpdate) {
                dataDrawerPubSub.emit(`${id}-UPDATE`)
            }
            return isUpdate
        },
        [envId, id]
    )

    const handleCreateRecord = useCallback(
        async (beforeId?: string) => {
            const isCreated = await createRecord({ envId, dsId: id, beforeId })
            if (isCreated) {
                dataDrawerPubSub.emit(`${id}-UPDATE`)
            }
            return isCreated
        },
        [createRecord, envId, id]
    )

    const handleDeleteRecord = useCallback(
        (recordIds: string[]) => {
            return deleteRecord({ envId, dsId: id, recordIds, mode: selectedMode, filter: resolvedFilter, linkFilter: resolvedLinkFilter })
        },
        [deleteRecord, envId, id, resolvedFilter, resolvedLinkFilter, selectedMode]
    )

    const handleDeleteMultipleRecord = useCallback(async () => {
        const isConfirm = await Modal.confirm({
            title: '确认删除',
            content: '删除后无法恢复',
            okStatus: 'error',
            okText: '删除'
        })
        if (isConfirm) {
            const isDelete = await handleDeleteRecord(selectedRecords)
            if (isDelete) {
                setState(draft => {
                    draft.selectedRecords = []
                    draft.selectedMode = SelectedMode.CURRENT_PAGE
                })
                selectedModeRef.current = SelectedMode.CURRENT_PAGE
                dataDrawerPubSub.emit(`${id}-UPDATE`)
            }
        }
    }, [handleDeleteRecord, id, selectedRecords, setState])

    const handleCreateField = useCallback(
        async (field: Field, sourceId: string, direction: InsertDirection) => {
            createFieldParamsRef.current = {
                sourceId,
                direction,
                fieldId: field.id
            }
            const innerType = await createField({
                envId,
                dsId: id,
                config: field,
                sourceId,
                direction
            })

            if (id !== field.dsId || createFieldNeedFetchData.has(field.type)) {
                await mutation({
                    envId,
                    dsId: id,
                    pagination: { currentPage: 1, pageSize: 100 },
                    filter: resolvedFilter,
                    linkFilter: resolvedLinkFilter
                })
            }
            return !!innerType
        },
        [createField, envId, id, mutation, resolvedFilter, resolvedLinkFilter]
    )

    const handleUpdateField = useCallback(
        async (fieldId: string, config: Field) => {
            const isUpdate = await updateField({
                envId,
                dsId: id,
                fieldId,
                config
            })
            const field = schema[fieldId]
            if ((field && field.type !== config.type) || updateFieldNeedFetchData.has(config.type)) {
                await mutation({
                    envId,
                    dsId: id,
                    pagination: { currentPage: 1, pageSize: 100 },
                    filter: resolvedFilter,
                    linkFilter: resolvedLinkFilter
                })
            }
            if (isUpdate) {
                dataDrawerPubSub.emit(`${id}-UPDATE`)
            }
            return !!isUpdate
        },
        [envId, id, mutation, resolvedFilter, resolvedLinkFilter, schema, updateField]
    )

    const handleSelectModeChange = useCallback(
        (val?: SelectedMode) => {
            if (val === SelectedMode.ALL) {
                setState(draft => {
                    draft.selectedMode = SelectedMode.ALL
                    draft.selectedRecords = recordPoints.map(p => p.id)
                })
                selectedModeRef.current = SelectedMode.ALL
                return
            }
            if (val === SelectedMode.CURRENT_PAGE) {
                setState(draft => {
                    draft.selectedMode = SelectedMode.CURRENT_PAGE
                    draft.selectedRecords = recordPoints.map(p => p.id)
                })
                selectedModeRef.current = SelectedMode.CURRENT_PAGE
                return
            }
            setState(draft => {
                draft.selectedMode = undefined
                draft.selectedRecords = []
            })
            selectedModeRef.current = undefined
        },
        [recordPoints, setState]
    )

    const handleDeleteField = useCallback(
        async (field: Field) => {
            const isConfirm = await Modal.confirm({
                title: '确认删除',
                content: `确认删除列「${field.name ?? '未命名列'}」？`,
                okStatus: 'error'
            })
            if (isConfirm) {
                const isDelete = await deleteField({
                    envId,
                    dsId: id,
                    fieldId: field.id
                })
                if (isDelete) {
                    dataDrawerPubSub.emit(`${id}-UPDATE`)
                }
                return !!isDelete
            }
            return false
        },
        [deleteField, envId, id]
    )

    const handleClose = useCallback(() => {
        setState(draft => {
            draft.selectedMode = undefined
            draft.selectedRecords = []
        })
    }, [setState])

    const uploadOptions = useMemo(
        () => ({
            info: { id: uploadId, label: name, groupId: id },
            options: uploadManagerInAppParams()
        }),
        [id, name, uploadId]
    )

    const videoUploadOptions = useMemo(() => {
        return {
            info: { id: uploadId, label: name, groupId: id },
            options: uploadManagerInAppParams()
        }
    }, [id, name, uploadId])

    const rowEvent: RowEvent = useMemo(
        () => ({
            onRecordSelect: handleSelectedRecords,
            onCellChange: handleCellChange,
            onCellUpdate: handleCellUpdate,
            onLoadMoreData: handleLoadDataSource,
            onCreateRecord: handleCreateRecord,
            onFetchPexelsList: getPexelsList
        }),
        [handleCellChange, handleCellUpdate, handleCreateRecord, handleLoadDataSource, handleSelectedRecords]
    )

    const columnEvent: ColumnEvent = useMemo(
        () => ({
            onCreateField: handleCreateField,
            onUpdateField: handleUpdateField,
            onDeleteField: handleDeleteField,
            onSelectModeChange: handleSelectModeChange
        }),
        [handleCreateField, handleUpdateField, handleDeleteField, handleSelectModeChange]
    )

    return (
        <>
            <SCxContainer>
                <Table
                    appId={appId}
                    headerFixed
                    viewIndependentData={viewIndependentData}
                    disableFieldCreatable={isAggregateDataSource}
                    disableSelectMode
                    disableSelect={isAggregateDataSource}
                    disableFindUse
                    contentLoading={loading}
                    data={dataSource}
                    dataSourceList={dataSourceList}
                    createFieldParams={createFieldParamsRef.current}
                    recordPoolAtom={recordPoolAtom}
                    selectedRecords={selectedRecords}
                    // disableFieldCreatable
                    // disableAddRecord
                    pageSize={100}
                    columnEvent={columnEvent}
                    rowEvent={rowEvent}
                    uploadOptions={uploadOptions}
                    videoUploadOptions={videoUploadOptions}
                    richTextUploadOptions={uploadManagerInAppParams()}
                    scrollBarOutside={false}
                />
            </SCxContainer>
            <SCxFooter>
                {selectedRecords.length > 0 && (
                    <SuspendPagination
                        selectIds={selectedRecords}
                        total={rowTotal}
                        mode={selectedMode}
                        enableDelete
                        onDelete={handleDeleteMultipleRecord}
                        onClose={handleClose}
                    />
                )}
            </SCxFooter>
        </>
    )
}
